import React from 'react';
import { Box } from '@mui/material';
import { tutorTokens } from '../../../theme/tutorTheme';

// Shared with MathTutorConfig — anything that reads a focus outline in this
// directory should point here rather than re-declare the same object, so a
// future contrast/offset tweak can't land in one surface and not the other.
export const focusRingSx = {
  '&:focus-visible': { outline: `2px solid ${tutorTokens.accent}`, outlineOffset: 2 },
};

// The one interactive "pick one option" tile behind subject, start mode,
// hint style, pacing and feedback style — in both the builder wizard and the
// post-publish config editor. Real radio semantics (role, aria-checked,
// keyboard Enter/Space) live here once so the two surfaces can't drift apart
// on selection behavior the way their previous separate implementations did.
//
// `hoverLift` toggles the wizard's denser-grid resting→hover translateY;
// MathTutorConfig's single-column list sets it false to keep its original,
// quieter hover (border-color only, no motion) — same interaction contract,
// each surface's existing feel preserved rather than homogenized.
//
// `role` defaults to "radio" (the "pick exactly one of a group" contract
// every existing caller uses) but can be overridden to "switch" for an
// independently-toggleable card — e.g. step 3's activity list, where more
// than one card is "on" at once. `aria-checked` reads correctly either way;
// only the announced role changes.
const SelectableTile = ({
  selected,
  disabled,
  onSelect,
  ariaLabel,
  hoverLift = true,
  role = 'radio',
  children,
  sx,
}) => {
  const handleKeyDown = (e) => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect();
    }
  };
  return (
    <Box
      role={role}
      aria-checked={selected}
      aria-disabled={disabled || undefined}
      aria-label={ariaLabel}
      tabIndex={disabled ? -1 : 0}
      onClick={() => !disabled && onSelect()}
      onKeyDown={handleKeyDown}
      sx={{
        p: 2,
        borderRadius: `${tutorTokens.radiusMd}px`,
        border: `1.5px solid ${selected ? tutorTokens.accent : tutorTokens.border}`,
        backgroundColor: selected ? tutorTokens.accentLight : tutorTokens.surfaceRaised,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        transition: `border-color ${tutorTokens.durFast}s ease, background-color ${tutorTokens.durFast}s ease${
          hoverLift ? `, transform ${tutorTokens.durFast}s ease` : ''
        }`,
        ...(disabled
          ? {}
          : {
              '&:hover': hoverLift
                ? { borderColor: tutorTokens.accent, transform: 'translateY(-1px)' }
                : { borderColor: tutorTokens.accent },
              ...(hoverLift
                ? {
                    '&:active': { transform: 'scale(0.98)' },
                    '@media (prefers-reduced-motion: reduce)': {
                      '&:hover': { transform: 'none' },
                      '&:active': { transform: 'none' },
                    },
                  }
                : {}),
            }),
        ...focusRingSx,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

export default SelectableTile;
